<template>
  <div class="business-page">
    <!-- ========== 顶部标题栏 ========== -->
    <div class="page-header">
      <div class="header-back" @click="router.push('/')">
        <nut-icon name="left" size="18" color="#3a3226"></nut-icon>
      </div>
      <span class="header-title">业务查询</span>
      <div class="header-placeholder"></div>
    </div>

    <!-- ========== 筛选与搜索区域 ========== -->
    <div class="filter-section">
      <!-- 自定义胶囊 Tabs -->
      <div class="pill-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          :class="['pill-btn', { active: activeTab === tab.key }]"
          @click="tabClick(tab.key)"
        >{{ tab.label }}</button>
      </div>

      <!-- 搜索栏 -->
      <div class="search-row">
        <div class="search-box">
          <nut-icon name="search" size="15" color="#bbb"></nut-icon>
          <input
            v-model="searchValue"
            type="text"
            placeholder="搜索用户/手机号"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <nut-icon
            v-if="searchValue"
            name="circle-ask"
            size="15"
            color="#ccc"
            class="search-clear"
            @click="searchValue = ''"
          ></nut-icon>
        </div>
        <button class="search-btn" @click="handleSearch">搜索</button>
      </div>
    </div>

    <!-- ========== 订单列表（滚动容器） ========== -->
    <div id="scroll" class="list-container">
      <!-- 首次加载中 -->
      <div v-if="isInitLoading" class="loading-init">
        <i class="loading-spin"></i>
        <span>加载中…</span>
      </div>

      <!-- 列表内容 -->
      <template v-else>
        <div v-for="(item, index) in orderList" :key="index" class="order-card">
          <!-- 卡片头部 -->
          <div class="card-top">
            <div class="user-info">
              <div class="avatar-box">
                <img :src="item.avatar || defaultAvatar" alt="" />
              </div>
              <div>
                <div class="user-name">{{ item.customerName }}</div>
                <div class="user-phone">{{ item.customerPhone }}</div>
              </div>
            </div>
			<span :class="['status-badge', statusColor[item.bizStatus] || 'pending']">
			  <i class="status-dot"></i>{{ $common.getLabelByValue(bizStatus,item.bizStatus,'dictValue','dictLabel') }}
			</span>
          </div>

          <!-- 核心指标 -->
          <div class="metrics-area">
            <div class="metric-item">
              <span class="metric-label">分期金额</span>
              <span class="metric-value" v-if="activeTab=='1'">¥{{ item.applyAmount }}</span>
			  <span class="metric-value" v-else>¥{{ item.loanApplyAmount }}</span>
            </div>
            <div class="metric-divider"></div>
            <div class="metric-item">
              <span class="metric-label">年利率</span>
              <span class="metric-value rate">{{ item.interestRate }}</span>
            </div>
            <div class="metric-divider"></div>
            <div class="metric-item">
              <span class="metric-label">期数</span>
              <span class="metric-value periods" v-if="activeTab == '1'">{{ item.approvedTerm }}期</span>
			  <span class="metric-value periods" v-else>{{ item.term }}期</span>
			  
            </div>
          </div>

          <!-- 详细信息 -->
          <div class="detail-section">
            <div class="detail-item">
              <span class="detail-label">分期项目</span>
              <span class="detail-value">{{ item.loanPurposeDesc }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">分期产品</span>
              <span class="detail-value">{{ item.productName }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">商户名称</span>
              <span class="detail-value">{{ item.merchantName }}</span>
            </div>
            <div class="detail-item no-border">
              <span class="detail-label">申请时间</span>
              <span class="detail-value detail-date">{{ item.createdAt }}</span>
            </div>
          </div>

          <!-- 卡片底部操作 -->
          <div class="card-actions">
            <!-- <button class="action-btn" @click="handleDetail(item)">查看详情</button> -->
          </div>
        </div>

        <!-- 加载更多区域 -->
        <div class="load-more-wrap">
          <div v-if="isLoading && orderList.length > 0" class="loading-more">
            <i class="loading-spin"></i>
            <span>加载中…</span>
          </div>
          <div v-else-if="!hasMore && orderList.length > 0" class="loading-end">
            <span>— 已加载全部数据 —</span>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-if="!isInitLoading && orderList.length === 0" class="empty-state">
          <nut-icon name="order" size="48" color="#ddd"></nut-icon>
          <p>暂无订单数据</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, getCurrentInstance, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { Toast } from '@nutui/nutui';
import homeIcon from '@/assets/imgs/item_icon.png';
import homeIconOn from '@/assets/imgs/item_icon_on.png';
import searchIcon from '@/assets/imgs/dt_icon.png';
import searchIconOn from '@/assets/imgs/dt_icon_on.png';
import myIcon from '@/assets/imgs/user_icon.png';
import myIconOn from '@/assets/imgs/user_icon_on.png';

const { proxy } = getCurrentInstance();
const router = useRouter();

// 默认头像
const defaultAvatar = 'https://img12.360buyimg.com/imagetools/jfs/t1/196430/38/8105/14329/60c806a4Ed506298a/e6de9fb7b8490f38.png';

// 顶部 Tabs
const tabs = [
  { key: '1', label: '申请订单' },
  { key: '2', label: '还款订单' },
  { key: '3', label: '完结订单' },
];

const statusColor = {
    'PENDING_SUBMIT': 'blue',
	'SUBMITTED': 'green',
	'RISK_REVIEW': 'green',
	'FIRST_TRIAL': 'green',
	'FINAL_REVIEW': 'org',
	'SUPPLEMENT': 'green',
	'APPROVED': 'org',
	'REJECTED': 'org',
	'WITHDRAWN': 'blue',
	'PENDING_SIGN': 'green',
	'SIGNING': 'blue',
	'SIGNED': 'org',
	'SIGN_FAILED': '无需签约',
	'NO_NEED': 'blue',
	'PENDING_LOAN': 'green',
	'PROCESSING_LOAN': 'green',
	'LOAN_SUCCESS': 'org',
	'LOAN_FAILED': 'org',
	'LOAN_REFUND': 'green',
	'NORMAL': 'org',
	'OVERDUE': 'org',
	'OVERDUE_COLLECTION': 'blue',
	'EXTENSION': 'blue',
	'PARTIAL_PAID': 'blue',
	'SETTLED': 'org',
	'WRITE_OFF': 'org',
	'BAD_DEBT': 'blue',
	'SETTLED_DERATE': 'blue' // 补充了最后一个
};

const activeTab = ref('1');
const searchValue = ref('');
const bottomTab = ref(1);

// ========== 滚动加载状态 ==========
const orderList = ref([]);
const hasMore = ref(true);
const isLoading = ref(false);
const isInitLoading = ref(false);
const pageNum = ref(1);
const pageSize = 10;
const totalCount = ref(0);
const bizStatus = ref([])

// ========== 底部导航切换 ==========
const tabClick = (type) => {
	pageNum.value = 1;
	activeTab.value = type
    getList(type,true)
};

const getDictData = (type)=>{
	return proxy.$axios.getDict(type).then(res=>{
		if(res.data.code == 200){
			return res.data.data
		}else{
			return []
		}
	})
}

// ========== 加载数据 ==========
const getList = async (type,isLoadMore = false) => {
  if (!isLoadMore) {
    pageNum.value = 1;
    isInitLoading.value = true;
  } else {
    isLoading.value = true;
  }

  try {
    const params = {
      pageNum: pageNum.value,
      pageSize: pageSize,
	  operatorId: localStorage.getItem('user')?JSON.parse(localStorage.getItem('user')).userId:''
    };
    if (searchValue.value) {
      params.searchKeyword = searchValue.value;
    }
	let res = ''
	orderList.value = []
	if(type == '1'){
		res = await proxy.$axios.applicationList(params);
	}else{
		if(type == '2'){
			params.lifecycleStatus = 'REPAYMENT'
		}else{
			params.lifecycleStatus = 'COMPLETED'
		}
		res = await proxy.$axios.orderList(params);
	}
    
    if (res.data.code === 200) {
      const data = res.data || {};
      const rows = data.rows || data.list || data.records || [];
      totalCount.value = data.total || 0;

      if (isLoadMore) {
        orderList.value = [...orderList.value, ...rows];
      } else {
        orderList.value = rows;
      }

      // 判断是否还有更多
      hasMore.value = orderList.value.length < totalCount.value;
      // 如果返回的数据不足一页，也说明没有更多了
      if (rows.length < pageSize) hasMore.value = false;
      pageNum.value++;
    }
  } catch (e) {
    console.error('加载列表失败:', e);
  } finally {
    isLoading.value = false;
    isInitLoading.value = false;
  }
};

// ========== 滚动加载更多 ==========
const handleScroll = () => {
  if (isLoading.value || !hasMore.value) return;

  const container = document.getElementById('scroll');
  if (!container) return;

  const { scrollTop, scrollHeight, clientHeight } = container;
  if (scrollTop + clientHeight >= scrollHeight - 80) {
    getList(activeTab.value,true);
  }
};

// ========== 搜索 ==========
const handleSearch = () => {
  pageNum.value = 1
  hasMore.value = true;
  orderList.value = [];
  nextTick(() => getList(activeTab.value,true));
};

// 操作
const handleDetail = (item) => {
  Toast.text('查看：' + item.name);
};

// ========== 初始加载 ==========
onMounted(async () => {
	bizStatus.value = await getDictData('biz_status')
  getList(activeTab.value,true);
  // 监听滚动事件
  nextTick(() => {
    const container = document.getElementById('scroll');
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }
  });
});
</script>

<style scoped>
/* ========== 页面整体 ========== */
.business-page {
  height: 100vh;
  background: #f5f3f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ========== 顶部标题栏 ========== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 46px;
  padding: 0 16px;
  background: #fff;
  flex-shrink: 0;
  border-bottom: 1px solid #f0ede8;
}
.header-back {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s;
}
.header-back:active {
  background: #f5f3f0;
}
.header-title {
  font-size: 17px;
  font-weight: 600;
  color: #3a3226;
  letter-spacing: 1px;
}
.header-placeholder {
  width: 32px;
}

/* ========== 筛选区域 ========== */
.filter-section {
  background: #fff;
  padding: 14px 16px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid #f0ede8;
}

/* 胶囊 Tabs */
.pill-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}
.pill-btn {
  flex: 1;
  height: 34px;
  border: none;
  border-radius: 17px;
  background: #f5f3f0;
  color: #999;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}
.pill-btn.active {
  background: linear-gradient(135deg, #8B7355, #a89070);
  color: #fff;
  box-shadow: 0 3px 10px rgba(139, 115, 85, 0.25);
}

/* 搜索栏 */
.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.search-box {
  flex: 1;
  display: flex;
  align-items: center;
  height: 38px;
  background: #f8f6f3;
  border-radius: 19px;
  padding: 0 14px;
  border: 1.5px solid transparent;
  transition: all 0.3s ease;
}
.search-box:focus-within {
  border-color: #C5A47E;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(197, 164, 126, 0.08);
}
.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #333;
  margin-left: 8px;
  font-family: inherit;
}
.search-input::placeholder {
  color: #c5c0b8;
  font-size: 13px;
}
.search-clear {
  cursor: pointer;
  padding: 4px;
}
.search-btn {
  height: 38px;
  padding: 0 20px;
  border: none;
  border-radius: 19px;
  background: linear-gradient(135deg, #8B7355, #a89070);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 3px 10px rgba(139, 115, 85, 0.2);
  transition: all 0.3s ease;
}
.search-btn:active {
  transform: scale(0.96);
}

/* ========== 列表容器（可滚动） ========== */
.list-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 16px;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 70px;
}

/* ========== 加载状态 ========== */
.loading-init {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  gap: 10px;
  font-size: 14px;
  color: #999;
}
.loading-more,
.loading-end {
  text-align: center;
  padding: 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.loading-more span,
.loading-end span {
  font-size: 13px;
  color: #bbb;
}
.loading-spin {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid #e8e5e0;
  border-top-color: #C5A47E;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========== 订单卡片 ========== */
.order-card {
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  margin-bottom: 14px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  animation: cardUp 0.4s ease-out;
}
@keyframes cardUp {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* 卡片头部 */
.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.avatar-box {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background: #f0ede8;
  flex-shrink: 0;
}
.avatar-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.user-name {
  font-size: 15px;
  font-weight: 600;
  color: #3a3226;
  margin-bottom: 2px;
  text-align: left;
}
.user-phone {
  font-size: 12px;
  color: #aaa;
}

/* 状态徽章 */
.status-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 12px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
}
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.status-badge.org {
  background: #fff7e6;
  color: #d48806;
}
.status-badge.org .status-dot{
  background: #faad14;
}
.status-badge.blue {
  background: #e6f7ff;
  color: #1890ff;
}
.status-badge.blue .status-dot{
  background: #1890ff;
}
.status-badge.green  {
  background: #f0f5f0;
  color: #5a8a6a;
}
.status-badge.green .status-dot{
  background: #52c41a;
}

/* 核心指标区 */
.metrics-area {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #fafaf8, #f5f3f0);
  border-radius: 12px;
  padding: 14px 10px;
  margin-bottom: 14px;
}
.metric-item {
  flex: 1;
  text-align: center;
}
.metric-label {
  display: block;
  font-size: 11px;
  color: #aaa;
  margin-bottom: 4px;
}
.metric-value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}
.metric-value.rate {
  color: #8B7355;
}
.metric-value.periods {
  color: #5a8a6a;
}
.metric-divider {
  width: 1px;
  height: 28px;
  background: #e8e5e0;
}

/* 详细信息 */
.detail-section {
  margin-bottom: 12px;
}
.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px dashed #f0ede8;
}
.detail-item.no-border {
  border-bottom: none;
}
.detail-label {
  font-size: 13px;
  color: #999;
}
.detail-value {
  font-size: 13px;
  color: #555;
  font-weight: 500;
  text-align: right;
  max-width: 60%;
}
.detail-date {
  color: #bbb;
  font-weight: 400;
}

/* 卡片操作按钮 */
.card-actions {
  display: flex;
  gap: 10px;
  padding-top: 4px;
}
.action-btn {
  flex: 1;
  height: 34px;
  border: 1.5px solid #e8e5e0;
  border-radius: 17px;
  background: #fff;
  color: #666;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  letter-spacing: 0.5px;
}
.action-btn:active {
  background: #f8f6f3;
}

/* ========== 空状态 ========== */
.empty-state {
  text-align: center;
  padding: 80px 0;
  color: #ccc;
}
.empty-state p {
  margin-top: 16px;
  font-size: 14px;
}
</style>
